import { useApiClient } from "@/composables/useApiClient";
import { useApiUonoClient } from "@/composables/useApiUonoClient";
import { useUtils } from "@/composables/useUtils";
import type {
  Address,
  Bike,
  Brand,
  Card,
  CardInfoPayload,
  ChecklistItem,
  Client,
  GetInsuranceData,
  InProgressInsurance,
  PaymentMethodChoice,
  PixOrderResult,
  PixPaymentStatus,
  Plan,
  ReportModelNotFoundPayload,
  SendBikeMediaPayload,
  SerialNumberRules,
} from "@/types/api";
import _ from "lodash";
import { defineStore } from "pinia";
import { useAppStore } from "./app";

function createLazyClient<T extends object>(factory: () => T): T {
  let instance: T | null = null;
  return new Proxy({} as T, {
    get(_target, prop, receiver) {
      if (!instance) {
        instance = factory();
      }
      return Reflect.get(instance, prop, receiver);
    },
  });
}

const apiClient = createLazyClient(() => useApiClient());
const apiUonoClient = createLazyClient(() => useApiUonoClient());

//TODO tipar corretamente
export interface ApiState {
  insuranceCode: string | null;
  clientId: number | null;
  client: Client | null;
  bike: Bike;
  brandName: string | null;
  planId: number | null;
  address: Address;
  bikeSerialNumber: string | null;
  bikeDANFECode: string | null;
  tag: string | null;
  storeCode: string | null;
  sellerContactId: string | number | null;
  isFreePlanEligible: boolean;
  plans: Plan[];
  planIds: number[];
  isClientCpfValid: boolean;
  card: Card;
  cardToken: string | null;
  checklist: ChecklistItem[];
  bikes: Bike[];
  isFinished: boolean;
  // props nao verificadas
  isUserValid: boolean;
  isUserCpfVerified: boolean;
  campaign: any;
  isCampaignAllowed: boolean | null;
  brands: Brand[];
  isCampaignEnabled: boolean | null;
  renewal: boolean | null;
  consultantPhone: string | null;
  isClient: boolean;
  resume: boolean;
  isCodeRequested: boolean;
  /** Cliente já optou pelo fluxo "pago sem ter a bike". */
  inspectionDeferred: boolean;
  /** Backend autoriza o fluxo "pago sem ter a bike" para este seguro. */
  eligibleForDeferredInspection: boolean;
  /** Serial, DANFE e fotos já foram enviados — inspeção concluída. */
  inspectionCompleted: boolean;
  isCompulsory: boolean;
  isMultibrand: boolean;
  campaignEligible: boolean;
  campaignPlanId: number | null;
  campaignMessage: string | null;
  paymentMethod: PaymentMethodChoice | null;
  /** Effective serial-number rules resolved by the backend (bike → brand → default). */
  serialRules: SerialNumberRules | null;
  otherInProgressJourneys: InProgressInsurance[];
}

function checkItemComplete(state: ApiState, item: string) {
  const check = state.checklist.find((c) => c.route === item);
  return check ? check.complete : false;
}

export const useApiStore = defineStore("api", {
  // props estão separadas para verificar quais estão sendo usadas e quais são desnecessárias
  state: (): ApiState => ({
    insuranceCode: null,
    clientId: null,
    client: null,
    bike: null,
    brandName: null,
    planId: null,
    address: {
      postalCode: null,
      street: null,
      complement: null,
      neighborhood: null,
      city: null,
      state: null,
      number: null,
    },
    bikeSerialNumber: null,
    bikeDANFECode: null,
    tag: null,
    storeCode: null,
    sellerContactId: null,
    isFreePlanEligible: false,
    isCompulsory: false,
    isMultibrand: false,
    campaignEligible: false,
    campaignPlanId: null,
    campaignMessage: null,
    paymentMethod: null,
    plans: [],
    planIds: [],
    isClientCpfValid: false,
    card: {
      number: null,
      holderName: null,
      expirationDate: null,
      cvv: null,
      brand: null,
      token: null,
      hash: null,
    },
    cardToken: null,
    bikes: [],
    isFinished: false,
    // props não verificadas
    isUserValid: false,
    isUserCpfVerified: false,
    campaign: null,
    isCampaignAllowed: null,
    isCampaignEnabled: null,
    renewal: null,
    consultantPhone: null,
    isClient: false,
    resume: false,
    isCodeRequested: false,
    inspectionDeferred: false,
    eligibleForDeferredInspection: false,
    inspectionCompleted: false,
    brands: [],
    checklist: [],
    serialRules: null,
    otherInProgressJourneys: [],
  }),

  getters: {
    bikesByType: (state) => (typeId: number) =>
      state.bikes.filter((bike) => bike.bikeType.id === typeId || typeId === -1),

    planBySlug: (state) => (slug: string) => {
      return state.plans.find((plan) => plan.slug === slug);
    },

    isItemComplete: (state) => (item: string) => checkItemComplete(state, item),

    isAllComplete: (state) => !_.some(state.checklist, ["complete", false]),
  },

  // Actions separadas para verificar quais estão sendo usadas mesmo no fluxo
  actions: {
    initInsurance(tag?: string, store?: string, sellerContactId?: string | number) {
      return apiClient.initInsurance(store, tag, sellerContactId).then((res) => {
        this.insuranceCode = res.data;
        localStorage.setItem("insuranceCode", this.insuranceCode);
        return res;
      });
    },
    getData() {
      return apiClient.getData(this.insuranceCode).then((res) => {
        const data = res.data;
        this.loadTagData(data);

        return res;
      });
    },
    loadTagData(data: GetInsuranceData) {
      if (data.client) {
        this.client = data.client;
      }

      // If bike is present, is a system bike loaded from tag
      if (data.bike) {
        this.bike = data.bike;
      }

      this.brandName = data.brandName ?? null;
      this.planId = data.planId;

      this.address = data.address;
      this.bikeSerialNumber = data.serialNumber;
      this.bikeDANFECode = data.danfeCode;
      this.tag = data.tag;
      this.inspectionDeferred = data.inspectionDeferred ?? false;
      this.eligibleForDeferredInspection = data.eligibleForDeferredInspection ?? false;
      this.inspectionCompleted = data.inspectionCompleted ?? false;
      this.isCompulsory = data.compulsory ?? false;
      this.isMultibrand = data.multibrand ?? false;
      this.campaignEligible = data.campaignEligible ?? false;
      this.campaignPlanId = data.campaignPlanId ?? null;
      this.campaignMessage = data.campaignMessage ?? null;
      this.paymentMethod = data.paymentMethod ?? null;
      this.renewal = data.renewal ?? false;
      this.consultantPhone = data.consultantPhone ?? null;
    },
    checkBrandFreePlanEligibility() {
      return apiClient.checkEligibleBrandFreePlan(this.insuranceCode).then((response) => {
        this.isFreePlanEligible = response.data;

        return this.isFreePlanEligible;
      });
    },
    listPlans() {
      return apiClient.listBikePlans(this.insuranceCode).then((response) => {
        const plans = response.data;
        this.plans = plans;
        return response;
      });
    },
    listBikeSkuPlans(bikeSku: string) {
      const brand = useAppStore().brand;
      apiClient.listBikePlansBySku(bikeSku, brand).then((response: any) => {
        const plans = response.data.data;
        this.plans = plans;
      });
    },
    async setPlans(planIds: number[]) {
      return apiClient.setPlans(this.insuranceCode, planIds).then((response) => {
        return response;
      });
    },
    async getPlanByInsurance() {
      return apiClient.getPlanByInsurance(this.insuranceCode).then((response) => {
        return response;
      });
    },
    setCPF(cpf: string, birthDate: string) {
      return apiClient.setCPF(this.client!.id, cpf, this.insuranceCode, birthDate).then((res) => {
        this.client!.cpf = cpf;
        this.client!.birthDate = birthDate;
        return res;
      });
    },
    verifyUserCpf(cpf: string, danfe: string) {
      return apiClient.checkClientCpf(cpf, danfe, this.insuranceCode).then((response) => {
        this.isClientCpfValid = response.data;

        return response;
      });
    },
    acceptTerms() {
      return apiClient.acceptTerms(this.insuranceCode);
    },
    setOwnerDanfe(ownerDanfeType: string) {
      return apiClient.setOwnerDafe(this.insuranceCode, ownerDanfeType);
    },
    getAddress(postalCode: string) {
      return apiClient.getCep(postalCode).then((response) => {
        const data = response.data;

        if ("erro" in data) {
          throw new Error("CEP não encontrado");
        }

        this.address = {
          postalCode: data.cep,
          street: data.logradouro,
          complement: data.complemento,
          neighborhood: data.bairro,
          city: data.localidade,
          state: data.uf,
          number: null,
        };

        return this.address;
      });
    },

    async setAddress(address: Address) {
      const addressDto = {
        zip: address.postalCode,
        street: address.street,
        number: address.number,
        complement: address.complement,
        district: address.neighborhood,
        city: address.city,
        state: address.state,
      };

      return apiClient.setAddress(this.insuranceCode, addressDto).then((res) => {
        this.address = address;
        return res;
      });
    },
    tokenizeCard(number: string, holderName: string, expirationDate: string, cvv: string) {
      const [month, year] = expirationDate.split("/");

      return apiClient
        .tokenCard({
          type: "card",
          card: {
            number: number.replace(/\s+/g, ""),
            holder_name: holderName,
            exp_month: month,
            exp_year: `20${year}`,
            cvv,
          },
        })
        .then((response) => {
          this.cardToken = response.data.id;
          this.card.brand = response.data.card.brand;

          return this.cardToken;
        });
    },
    async setCardInfo(cardInfo: CardInfoPayload) {
      return apiClient.setCardInfo(this.insuranceCode!, cardInfo).then((res) => {
        return res;
      });
    },
    setPaymentMethod(method: PaymentMethodChoice, installments: number) {
      return apiClient.setPaymentMethod(this.insuranceCode!, method, installments).then((res) => {
        this.paymentMethod = method;
        return res;
      });
    },
    createPixPayment() {
      return apiClient.createPixPayment(this.insuranceCode!).then((res) => {
        return res.data as PixOrderResult;
      });
    },
    getPixPaymentStatus() {
      return apiClient.getPixPaymentStatus(this.insuranceCode!).then((res) => {
        return res.data as PixPaymentStatus;
      });
    },
    checkIfBikeHasTag(bikeSerialNumber: string) {
      const brand = useAppStore().brand;

      return apiClient
        .checkIfBikeHasTag(bikeSerialNumber, brand, this.insuranceCode)
        .then((res) => {
          return res.data;
        });
    },
    getChecklist() {
      return apiClient.getChecklist(this.insuranceCode!).then((res) => {
        this.checklist = res.data;
        return {
          ...res,
          isAllComplete: !_.some(res.data, ["complete", false]),
        };
      });
    },
    createUser(name: string, email: string, phone: string) {
      return apiClient.setUser({ name, email, phone }, this.insuranceCode).then((response) => {
        const clientId =
          typeof response.data === "number" ? response.data.toString() : response.data;
        this.client = {
          ...this.client,
          id: response.data.toString(),
          name,
          email,
          phone,
        };

        return this.client.id;
      });
    },
    sendBikeSerialNumber(bikeSerialNumber: string) {
      return apiClient.setBikeSerialNumber(this.insuranceCode!, bikeSerialNumber).then((res) => {
        return res;
      });
    },
    async loadSerialRules() {
      const res = await apiClient.getBikeSerialRules(this.insuranceCode!);
      this.serialRules = res.data ?? null;
      return this.serialRules;
    },
    sendUserValidation(email: string) {
      const brand = useAppStore().brand;
      return apiClient.resendUserVerification(email, brand, this.insuranceCode).then((response) => {
        return response;
      });
    },
    confirmClientForJourney(code: string) {
      return apiClient
        .confirmClientForJourney(this.client.id, code, this.insuranceCode)
        .then((response) => {
          if (response && response.data && typeof response.data === "object") {
            const clientData = response.data as Client;
            this.client = {
              ...this.client,
              name: clientData.name,
              phone: clientData.phone,
              cpf: clientData.cpf,
            };
          }
          return response;
        });
    },
    listBikes() {
      return apiClient.listBikes(this.insuranceCode).then((response) => {
        const bikes = response.data;

        bikes.sort((a: Bike, b: Bike) => a.model.localeCompare(b.model));
        this.bikes = bikes;
        return response;
      });
    },
    loadDanfe(danfeCode: string) {
      return apiClient.loadDanfe(this.insuranceCode, danfeCode);
    },
    loadBrandData() {
      return apiClient.loadBrandData(this.insuranceCode);
    },
    setBike(bikeId: number) {
      return apiClient.setBike(this.insuranceCode, bikeId).then(async (res) => {
        this.bike = res.data.bike;
        this.campaign = res.data.campaign;
        return res;
      });
    },
    reportModelNotFound(payload: ReportModelNotFoundPayload) {
      return apiClient.reportModelNotFound(this.insuranceCode, payload).then((res) => {
        if (res.data.bike) {
          this.bike = res.data.bike;
        }
        return res;
      });
    },
    getInsuranceBike() {
      return apiClient.getInsuranceBike(this.insuranceCode!).then((res) => {
        this.bike = res.data;
        return res;
      });
    },
    setSelfie(selfie: string) {
      return apiClient.setSelfie(this.insuranceCode, selfie);
    },
    sendBikeMedia({ type, file }: SendBikeMediaPayload) {
      return apiClient.sendBikeMedia(this.insuranceCode, type, file);
    },
    finish() {
      return apiClient.finish(this.insuranceCode!).then((res) => {
        this.isFinished = res.data;
        return res.data;
      });
    },
    /**
     * Marca o seguro como elegível ao fluxo "pago sem ter a bike". Falha (422)
     * se o seguro for grátis, compulsório ou upgrade — o front deve manter o
     * toast de "Volte depois" nesse caso.
     */
    deferInspection() {
      return apiClient.deferInspection(this.insuranceCode!).then((res) => {
        this.inspectionDeferred = true;
        return res.data;
      });
    },
    cancelDeferInspection() {
      return apiClient.undoDeferInspection(this.insuranceCode!).then((res) => {
        this.inspectionDeferred = false;
        return res.data;
      });
    },
    sequenceScreenFlow(path: string, isNext: boolean) {
      return apiClient
        .sequenceScreenFlow(
          path,
          isNext,
          this.insuranceCode,
          this.clientId ?? undefined,
          useUtils().channel,
        )
        .then((res) => res);
    },
    setHasInsurance(tag: string) {
      return apiUonoClient.setHasInsurance(tag);
    },
    sendRating(insuranceCode: string, rating: string, comment: string) {
      return apiClient.sendRating({ insuranceCode, rating, comment }).then((response) => {
        return response;
      });
    },
    loadMultibrandBrands(scooter?: boolean) {
      return apiClient.listMultibrandBrands(scooter).then((res: any) => {
        // Backend returns the list directly, not wrapped in { data: ... }
        this.brands = Array.isArray(res) ? res : res.data;
        return res;
      });
    },
    setInsuranceBrand(brandId: number) {
      return apiClient.setInsuranceBrand(this.insuranceCode!, brandId);
    },
    async attachTagData(tag: string) {
      const response = await apiClient.attachTagData(this.insuranceCode!, tag);
      this.tag = tag;
      if (response.data.hasClientData) {
        this.clientId = response.data.clientId ?? null;
      }
      return response.data;
    },
    async listInProgressJourneys(phone: string) {
      const response = await apiClient.listInProgressJourneys(phone);
      this.otherInProgressJourneys = response.data;
      return this.otherInProgressJourneys;
    },
    async createClient(name: string, email: string, phone: string) {
      const response = await apiClient.createClient(name, email, phone);
      this.clientId = response.data.clientId;
      this.client = {
        id: String(response.data.clientId),
        name,
        email,
        phone,
        cpfName: null,
        cpf: null,
        birthDate: null,
        rg: null,
        cnh: null,
        rne: null,
        createdAt: "",
        updatedAt: "",
        type: null,
      };
      return this.clientId;
    },
    async verifyClientCode(code: string) {
      const response = await apiClient.verifyClientCode(this.clientId!, code);
      this.client = response.data;
      return this.client;
    },
    async resolveTagEntry(tag?: string, storeCode?: string, sellerContactId?: string | number) {
      this.tag = tag ?? null;
      this.storeCode = storeCode ?? null;
      this.sellerContactId = sellerContactId ?? null;
      const response = await apiClient.resolveTagEntry(storeCode, tag, sellerContactId);
      if (response.data.hasClientData) {
        this.clientId = response.data.clientId;
        this.client = {
          id: String(response.data.clientId),
          name: response.data.clientName ?? "",
          email: response.data.clientEmail ?? "",
          phone: response.data.clientPhone ?? "",
          cpfName: null,
          cpf: null,
          birthDate: null,
          rg: null,
          cnh: null,
          rne: null,
          createdAt: "",
          updatedAt: "",
          type: null,
        };
      }
      return response.data;
    },
    async startNewInsuranceForClient() {
      const response = await apiClient.startNewInsuranceForClient(
        this.clientId!,
        this.storeCode ?? undefined,
        this.tag ?? undefined,
        this.sellerContactId ?? undefined,
      );
      this.insuranceCode = response.data;
      localStorage.setItem("insuranceCode", this.insuranceCode);
      return this.insuranceCode;
    },
    async ensureInsuranceForClient() {
      const response = await apiClient.ensureInsuranceForClient(
        this.clientId!,
        this.storeCode ?? undefined,
        this.tag ?? undefined,
        this.sellerContactId ?? undefined,
      );
      if (response.data) {
        this.insuranceCode = response.data;
        localStorage.setItem("insuranceCode", this.insuranceCode);
      }
      return this.insuranceCode;
    },
    requestConfirmationForInsurance(insuranceCode: string) {
      return apiClient
        .sendConfirmationCode(insuranceCode)
        .then(() => (this.isCodeRequested = true));
    },
  },
  persist: {
    storage: sessionStorage,
  },
});

export type UseApiStore = ReturnType<typeof useApiStore>;
